import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	inject,
	OnDestroy,
	OnInit,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { mergeMap, Observable, Subject } from 'rxjs';
import { AuRightSidebarService } from '@core/services/au-right-sidebar.service';
import { IconSnackBarService } from '@core/services/icon-snack-bar.service';
import { filter, take, takeUntil, tap } from 'rxjs/operators';
import { AuUtilsFunctions } from '@app/shared';
import { iconEnum } from '@app/configs/model/IconEnum';
import { NgxTolgeeModule, TranslateService } from '@tolgee/ngx';
import {
	IIncident,
	IIncidentCreateVariables,
} from '@app/pages/cmms/interfaces/incidents.interface';
import { Store } from '@ngrx/store';
import {
	clearIncidentExternalData,
	startCreateIncident,
} from '@app/pages/cmms/components/incident/state/incidents.actions';
import { IIncidentState } from '@app/pages/cmms/components/incident/state/incidents.reducer';
import { CmmsPages } from '@app/pages/cmms/state/cmms.reducer';
import { RightSidebarNavigationService } from '@app/layout/au-right-sidebar/right-sidebar-navigation.service';
import { ACTIVE_PAGE_CONFIG } from '@app/pages/cmms/configs/active-page.config';
import { TableType } from '@app/pages/cmms/types';
import {
	SetAttachFormValue,
	SetRightSidebarComponentContext,
} from '@layout/state/layout.actions';
import { ICMMSColumn } from '@shared/ui-components/au-table-layout/utils/interfaces';
import {
	attachedEntityTableConfig,
	sidebarHeaders,
} from '@app/pages/cmms/configs/cmms-table-configs';
import { IRequest } from '@app/pages/cmms/interfaces/request.interfaces';
import {
	filterEntityByKey,
	getPrivateKeys,
} from '@app/pages/cmms/utils-and-classes/cmms-util-functions';
import { WorkOrdersService } from '@app/pages/cmms/services/work-orders.service';
import { IncidentsService } from '@app/pages/cmms/services/incidents.service';
import { RequestsService } from '@app/pages/cmms/services/requests.service';
import { EntityActionType, EntityType } from '@layout/state/layout.reducer';
import { editFileSuccess } from '@app/pages/cmms/components/files/state/files.action';
import { FilesService } from '@app/pages/cmms/services/files.service';
import { IDroppedFile } from '@shared/ui-components/au-file-loader/au-file-loader.interfaces';
import {
	AssetMappingEntityTypes,
	ICmmsAsset,
} from '@app/pages/cmms/interfaces/assets.interfaces';
import { incidentData } from '@app/pages/cmms/components/incident/state/incidents.selectors';
import { IFileLoaderOptions } from '@shared/ui-components/au-file-loader/services/file-loader.service';
import { RightSidebarAliasesEnum } from '@layout/au-right-sidebar/au-right-sidebar-component-aliases';
import { AssetsService } from '@app/pages/cmms/services/assets.service';
import { fileTypesConfig } from '@app/pages/cmms/configs/file-filter.config';
import { CommonModule } from '@angular/common';
import { CmmsEntityNameComponent } from '../../shared-form-group-controls/cmms-entity-name/cmms-entity-name.component';
import { CmmsLocationsSectionComponent } from '../../shared-form-group-controls/cmms-locations-section/cmms-locations-section.component';
import { CmmsPriorityLevelComponent } from '../../shared-form-group-controls/cmms-priorirty-level/cmms-priority-level.component';
import { CmmsEntityCategoryComponent } from '../../shared-form-group-controls/cmms-entity-category/cmms-entity-category.component';
import { CmmsAssigeeAndApproverComponent } from '../../shared-form-group-controls/cmms-assignees-and-approver/cmms-assigee-and-approver.component';
import { CmmsDatePickerComponent } from '../../shared-form-group-controls/cmms-date-picker/cmms-date-picker.component';
import { CmmsLabelsSectionComponent } from '../../shared-form-group-controls/cmms-labels-section/cmms-labels-section.component';
import { CmmsDescriptionComponent } from '../../shared-form-group-controls/cmms-description/cmms-description.component';
import { MaintenanceSectionComponent } from '../../maintenance-section/maintenance-section.component';
import { AuMaintenanceSelectorComponent } from '../../au-maintenance-selector/au-maintenance-selector.component';
import { AuCmmsCostComponent } from '../../au-cmms-cost/au-cmms-cost.component';
import { EntitySectionComponent } from '../../entity-section/entity-section.component';
import { AssetsSectionComponent } from '../../assets-section/assets-section.component';
import { IWorkOrder } from '@app/domain/cmms-types/workorder-types';
import { AuFileLoaderComponent } from '@app/shared/ui-components/au-file-loader/au-file-loader.component';

@Component({
	selector: 'new-incident',
	templateUrl: './new-incident.component.html',
	styleUrls: ['./new-incident.component.scss'],
	imports: [
		CommonModule,
		ReactiveFormsModule,
		CmmsEntityNameComponent,
		CmmsLocationsSectionComponent,
		CmmsPriorityLevelComponent,
		CmmsEntityCategoryComponent,
		CmmsAssigeeAndApproverComponent,
		CmmsDatePickerComponent,
		CmmsLabelsSectionComponent,
		CmmsDescriptionComponent,
		MaintenanceSectionComponent,
		AuMaintenanceSelectorComponent,
		AuCmmsCostComponent,
		EntitySectionComponent,
		AssetsSectionComponent,
		AuFileLoaderComponent,
		NgxTolgeeModule,
	],
})
export class NewIncidentComponent implements OnInit, OnDestroy, AfterViewInit {
	incidentForm: FormGroup = this.fb.group({
		requests: [],
		workOrders: [],
		files: [[]],
		assets: [],
		coordinates: [],
	});

	isFullscreen$: Observable<boolean>;

	actionType: EntityActionType;
	editEntityType: EntityType;
	currentEntityDataForEdit: any;
	externalData: Partial<IIncident>;
	fileLoaderOptions: Partial<IFileLoaderOptions> = {
		fileTypesConfig,
		mutationVariables: {
			isCreationInProgress: true,
		},
	};

	private destroy$: Subject<void> = new Subject<void>();
	private initialFormValue: any;
	private isProcessing: boolean;

	readonly iconEnum = iconEnum;
	readonly cmmsPages = CmmsPages;
	readonly tableType = TableType;

	readonly tableConfig: ICMMSColumn[] = attachedEntityTableConfig;

	private readonly translateService = inject(TranslateService);

	constructor(
		private sidebarNavigation: RightSidebarNavigationService,
		private fb: FormBuilder,
		private sidebarService: AuRightSidebarService,
		private notify: IconSnackBarService,
		private store$: Store<IIncidentState>,
		private workOrdersService: WorkOrdersService,
		private incidentService: IncidentsService,
		private requestService: RequestsService,
		private filesService: FilesService,
		private assetsService: AssetsService,
		private cdr: ChangeDetectorRef
	) {}

	ngOnInit() {
		this.isFullscreen$ = this.sidebarService.sidebarFullscreen$;
		this.setConfigurationForComponent();
		this.trackSidebarSubmitAction();
		this.trackSidebarCloseAction();
	}

	ngAfterViewInit() {
		this.getFormValueFromStore();

		this.store$
			.select(incidentData)
			.pipe(takeUntil(this.destroy$))
			.subscribe(data => {
				if (data) {
					this.externalData = data;
					this.incidentForm.patchValue({
						locations: data.locations,
						coordinates: this.externalData.coordinates,
					});
					this.initialFormValue = this.incidentForm.value;
				}
			});
	}

	addChildControl(name: string, control: FormControl) {
		this.incidentForm.addControl(name, control);
		this.initialFormValue = this.incidentForm.value;
	}

	extractMutationVariablesFromForm(): IIncidentCreateVariables {
		const formValue = this.incidentForm.value;

		const payload: any = {
			coordinates: formValue.coordinates,
			name: formValue.name,
			priority: formValue.priority,
			approvedBy: formValue.approver?.psId,
			assignee: formValue.assignee?.psId,
			deadline: formValue.deadline,
			occurredAt: formValue.occurredAt,
			description: formValue.description,
			category: formValue.category,
			locations: formValue.locations,
			files: getPrivateKeys(formValue.files),
			requests: getPrivateKeys(formValue.requests),
			workOrders: getPrivateKeys(formValue.workOrders),
			assets: formValue.assets?.map(attachedAsset => {
				return { id: attachedAsset.pk, name: attachedAsset.name };
			}),
			deviationType: formValue.deviationType,
			riskLevel: formValue.riskLevel,
			consequenceRating: formValue.consequenceRating,
			costAllocation: formValue.costAllocation,
			timeframe: formValue.timeframe,
			costOfRepair: formValue.costOfRepair,
			labels: formValue.labels,
		};

		if (!payload.costOfRepair) {
			payload.costOfRepair = null;
		}

		return payload;
	}

	occurredAtChanged(date: Date) {
		this.incidentForm.controls.occurredAt.setValue(date);
	}

	addNewIncident(variables: IIncidentCreateVariables) {
		this.store$.dispatch(
			startCreateIncident({ variables, skipTableReload: !!this.externalData })
		);
	}

	trackSidebarSubmitAction(): void {
		this.sidebarService.sidebarActionBtnClicked$
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				//TODO this is a temporary fix for multiple click on submit button
				// will be rewritten in future workspace refactoring
				if (this.isProcessing) {
					return;
				}
				this.isProcessing = true;

				if (!this.incidentForm.valid) {
					this.incidentForm.markAllAsTouched();
					this.notify.warning(this.translateService.instant('required_fields_warning'));
					this.isProcessing = false;
					return;
				}

				if (this.actionType === 'EDIT') {
					this.attachToExisting();
				} else if (this.actionType === 'NEW') {
					this.attachForNew();
				} else if (this.incidentForm.valid) {
					this.addNewIncident(this.extractMutationVariablesFromForm());
					this.sidebarService.clearNavigationHistory();
				}
			});
	}

	trackSidebarCloseAction(): void {
		this.sidebarService.sidebarIsClosing$.pipe(takeUntil(this.destroy$)).subscribe(() => {
			if (this.formNotChanged()) {
				this.sidebarService.closeAndClearPortal();
			} else {
				this.sidebarService.alertAboutDataLoss(this.incidentForm.value);
			}
		});
	}

	formNotChanged(): boolean {
		return !AuUtilsFunctions.hasBeenFilled(
			this.initialFormValue,
			this.incidentForm.value
		);
	}

	navigateTo(page: CmmsPages) {
		this.sidebarNavigation.setInitialComponent({
			initialComponent: RightSidebarAliasesEnum.newIncident,
			config: ACTIVE_PAGE_CONFIG[CmmsPages.newIncident].sidebarConfig,
			formValue: AuUtilsFunctions.deepCopyObject(this.incidentForm.value),
			initialPage: CmmsPages.newIncident,
		});

		this.store$.dispatch(
			SetRightSidebarComponentContext({
				componentContext: {
					actionType: 'NEW',
					currentEntityForEdit: this.incidentForm.value,
				},
			})
		);

		this.sidebarNavigation.navigateTo(page);
	}

	navigateToExist(pageData: { page: CmmsPages; tableType: TableType }) {
		this.setNavigationConfigForHistory();

		this.store$.dispatch(
			SetRightSidebarComponentContext({
				componentContext: {
					tableTypeForExistingEntity: pageData.tableType,
					currentEntityForEdit: this.incidentForm.value,
					editEntityType: 'INCIDENTS',
				},
			})
		);

		this.sidebarNavigation.navigateTo(pageData.page, sidebarHeaders[pageData.tableType]);
	}

	openAssets() {
		this.setNavigationConfigForHistory();

		this.store$.dispatch(
			SetRightSidebarComponentContext({
				componentContext: {
					tableTypeForExistingEntity: TableType.ASSETS,
					currentEntityForEdit: this.incidentForm.value,
					editEntityType: 'INCIDENTS',
				},
			})
		);

		this.sidebarNavigation.navigateTo(
			CmmsPages.existingEntities,
			sidebarHeaders[TableType.ASSETS]
		);
	}

	setNavigationConfigForHistory() {
		this.sidebarNavigation.setInitialComponent({
			initialComponent: RightSidebarAliasesEnum.newIncident,
			config: ACTIVE_PAGE_CONFIG[CmmsPages.newIncident].sidebarConfig,
			formValue: AuUtilsFunctions.deepCopyObject(this.incidentForm.value),
			initialPage: CmmsPages.newIncident,
		});
	}

	getFormValueFromStore() {
		this.sidebarNavigation
			.getInitialFormValueByPage(CmmsPages.newIncident)
			.subscribe(sideBar => {
				this.incidentForm.patchValue(sideBar.formValue);
			});
	}

	viewSelected(item: { data: any; entityType: CmmsPages }) {
		this.setNavigationConfigForHistory();
		this.sidebarService.viewCurrentEntity(
			NewIncidentComponent,
			CmmsPages.newIncident,
			item
		);
		this.store$.dispatch(SetAttachFormValue({ formValue: this.incidentForm.value }));
	}

	unlinkItem(
		item: IWorkOrder | IRequest | ICmmsAsset,
		itemType: 'workOrders' | 'requests' | 'assets'
	): void {
		this.incidentForm.value[itemType] = filterEntityByKey<typeof item>(
			this.incidentForm.value[itemType],
			'pk',
			item.pk
		);
		this.cdr.markForCheck();
	}

	setConfigurationForComponent() {
		this.sidebarNavigation.pageContext
			.pipe(
				takeUntil(this.destroy$),
				filter(context => !!context)
			)
			.subscribe(context => {
				this.actionType = context.actionType;
				this.editEntityType = context.editEntityType;
				this.currentEntityDataForEdit = context.currentEntityForEdit;
			});
	}

	attachToExisting() {
		let successMessage;

		this.incidentService
			.createIncidents(this.extractMutationVariablesFromForm())
			.pipe(
				take(1),
				mergeMap(({ incident }) => {
					const payload = {
						pk: this.currentEntityDataForEdit.pk,
						incidents: [incident.pk],
						name: this.currentEntityDataForEdit.name,
						description: this.currentEntityDataForEdit.description,
						id: null,
						notes: null,
					};
					let observable;

					if (this.editEntityType === 'WO') {
						successMessage = `${this.translateService.instant('work_order')} ${this.translateService.instant('has_been_updated_successfully')}`;
						observable = this.workOrdersService.updateWorkOrder(payload);
					} else if (this.editEntityType === 'REQUEST') {
						successMessage = `${this.translateService.instant('request')} ${this.translateService.instant('has_been_updated_successfully')}`;
						observable = this.requestService.editRequest(payload);
					} else if (this.editEntityType === 'ASSET') {
						successMessage = `${this.translateService.instant('asset')} ${this.translateService.instant('has_been_updated_successfully')}`;
						observable = this.assetsService.linkAssetsObjectsVariables({
							assets: [this.currentEntityDataForEdit.pk],
							incidents: [incident.pk],
						});
						// Adds the linked incident to the asset's incidents list

						this.assetsService.afterMappingAssetSuccess(
							this.currentEntityDataForEdit,
							AssetMappingEntityTypes.Incident,
							[...this.currentEntityDataForEdit.incidents, incident],
							false
						);
					} else if (this.editEntityType === 'FILE') {
						payload.id = this.currentEntityDataForEdit.id;
						payload.notes = this.currentEntityDataForEdit.notes;

						successMessage = `${this.translateService.instant('file')} ${this.translateService.instant('has_been_updated_successfully')}`;
						observable = this.filesService.editFile(payload).pipe(
							tap(file => {
								this.store$.dispatch(editFileSuccess({ file }));
							})
						);
					}
					return observable;
				})
			)
			.subscribe(() => {
				if (this.editEntityType != 'ASSET') {
					this.notify.success(successMessage);
				}

				this.setActionTypeToEmpty();
				this.sidebarNavigation.navigateToPreviousPage();
			});
	}

	attachForNew() {
		this.incidentService
			.createIncidents(this.extractMutationVariablesFromForm())
			.pipe(take(1))
			.subscribe(({ incident }) => {
				this.store$.dispatch(
					SetAttachFormValue({
						formValue: { incidents: [incident] },
					})
				);

				this.setActionTypeToEmpty();
				this.sidebarNavigation.navigateToPreviousPage();
			});
	}

	private setActionTypeToEmpty() {
		this.store$.dispatch(
			SetRightSidebarComponentContext({
				componentContext: {
					actionType: null,
				},
			})
		);
	}

	fileListChanged($event: Partial<IDroppedFile>[]) {
		this.filesService.addOnlyNewFilesToForm($event, this.incidentForm);
	}

	filesDeleted({ ids, onSuccess }: { ids: string[]; onSuccess: () => void }) {
		this.filesService.deleteFileFromFormValue(ids, this.incidentForm);
		onSuccess();
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
		if (this.externalData) {
			this.store$.dispatch(clearIncidentExternalData());
		}
	}

	protected readonly fileTypesConfig = fileTypesConfig;
}
